import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import KeyRoundedIcon from "@mui/icons-material/KeyRounded";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import WifiRoundedIcon from "@mui/icons-material/WifiRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import ShareRoundedIcon from "@mui/icons-material/ShareRounded";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";

import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import {
  useEffect,
  useMemo,
  useState,
} from "react";

import { useNavigate } from "react-router-dom";

import {
  Controller,
  useForm,
} from "react-hook-form";

import { SoftDataGrid } from "../../../components/data/SoftDataGrid";
import { getBrands } from "../../../services/brandsService";
import {
  createShop,
  deleteShop,
  generateShopApiKey,
  getShop,
  getShops,
  updateShop,
} from "../../../services/shopsService";
import { getApiErrorMessage } from "../../../utils/apiData";

const fieldSx = {
  "& .MuiInputBase-root": {
    minHeight: 38,
    borderRadius: 0,
    fontSize: "0.72rem",
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.72rem",
  },
  "& .MuiFormHelperText-root": {
    mx: 0,
    fontSize: "0.63rem",
  },
};

function rowsFrom(payload) {
  const paginator =
    payload?.shops ??
    payload?.data?.shops ??
    payload?.data ??
    payload;

  if (Array.isArray(paginator?.data)) {
    return paginator.data;
  }

  if (Array.isArray(paginator)) {
    return paginator;
  }

  return [];
}

function totalFrom(payload) {
  const paginator =
    payload?.shops ??
    payload?.data?.shops ??
    payload?.data ??
    payload;

  if (typeof paginator?.total === "number") {
    return paginator.total;
  }

  if (Array.isArray(paginator?.data)) {
    return paginator.data.length;
  }

  if (Array.isArray(paginator)) {
    return paginator.length;
  }

  return 0;
}

function brandsFrom(payload) {
  const paginator =
    payload?.brands ??
    payload?.data?.brands ??
    payload?.data ??
    payload;

  if (Array.isArray(paginator?.data)) {
    return paginator.data;
  }

  if (Array.isArray(paginator)) {
    return paginator;
  }

  return [];
}

function initials(name) {
  return String(name || "SH")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

function brandName(shop) {
  if (typeof shop?.brand === "string") {
    return shop.brand;
  }

  return shop?.brand?.name ?? shop?.brand_name ?? "No brand";
}

function credentialText(api, shop) {
  return [
    "Spend2Connect Shop API Credentials",
    "==================================",
    "",
    `Shop: ${shop?.shop_name || ""}`,
    `Shop code: ${shop?.shop_code || ""}`,
    `Shop ID: ${shop?.id || ""}`,
    "",
    `Endpoint: ${api?.url || ""}`,
    `Header: ${api?.header || ""}`,
    `API key: ${api?.api_key || ""}`,
    "",
    api?.warning ||
      "Store this key securely. It will not be shown again.",
  ].join("\n");
}

function credentialFilename(shop) {
  const code = String(shop?.shop_code || "shop")
    .replace(/[^a-zA-Z0-9_-]/g, "_")
    .toLowerCase();

  return `${code}-api-credentials.txt`;
}

function downloadCredentials(api, shop) {
  const blob = new Blob([credentialText(api, shop)], {
    type: "text/plain;charset=utf-8",
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = credentialFilename(shop);

  document.body.appendChild(link);
  link.click();
  link.remove();

  URL.revokeObjectURL(url);
}

async function shareCredentials(api, shop) {
  const file = new File(
    [credentialText(api, shop)],
    credentialFilename(shop),
    {
      type: "text/plain;charset=utf-8",
    },
  );

  if (
    navigator.share &&
    (!navigator.canShare ||
      navigator.canShare({ files: [file] }))
  ) {
    await navigator.share({
      title: "Spend2Connect Shop API Credentials",
      text: `${shop?.shop_name || "Shop"} API credentials`,
      files: [file],
    });

    return true;
  }

  return false;
}

function SquareDialog({
  open,
  onClose,
  title,
  subtitle,
  children,
  actions,
  maxWidth = "md",
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 0,
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: "1px solid #E3E7EC",
        }}
      >
        <Typography
          sx={{
            fontSize: "0.92rem",
            fontWeight: 850,
          }}
        >
          {title}
        </Typography>

        {subtitle ? (
          <Typography
            sx={{
              mt: 0.25,
              color: "#667085",
              fontSize: "0.67rem",
            }}
          >
            {subtitle}
          </Typography>
        ) : null}
      </DialogTitle>

      <DialogContent
        sx={{
          p: 2.2,
          bgcolor: "#FBFCFD",
        }}
      >
        {children}
      </DialogContent>

      {actions ? (
        <DialogActions
          sx={{
            borderTop: "1px solid #E3E7EC",
          }}
        >
          {actions}
        </DialogActions>
      ) : null}
    </Dialog>
  );
}

function ShopForm({
  shop,
  brands,
  brandsLoading,
  brandsError,
  busy,
  onCancel,
  onSubmit,
}) {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      shop_code: shop?.shop_code || "",
      shop_name: shop?.shop_name || "",
      brand_id: shop?.brand_id || shop?.brand?.id || "",
      location: shop?.location || "",
      public_ip_address: shop?.public_ip_address || "",
      is_active: shop?.is_active ?? true,
    },
  });

  const submit = (values) => {
    onSubmit({
      shop_code: values.shop_code.trim(),
      shop_name: values.shop_name.trim(),
      brand_id: values.brand_id,
      location: values.location.trim() || null,
      public_ip_address:
        values.public_ip_address.trim() || null,
      is_active: Boolean(values.is_active),
    });
  };

  return (
    <Box component="form" onSubmit={handleSubmit(submit)}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "220px 1fr",
          },
          gap: 2,
        }}
      >
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            borderRadius: 0,
            textAlign: "center",
          }}
        >
          <Avatar
            sx={{
              width: 92,
              height: 92,
              mx: "auto",
              bgcolor: "#EEF3FF",
              color: "#2F5BEA",
              fontWeight: 850,
            }}
          >
            {initials(shop?.shop_name || "New Shop")}
          </Avatar>

          <Typography
            sx={{
              mt: 1.4,
              fontSize: "0.78rem",
              fontWeight: 800,
            }}
          >
            {shop ? "Shop profile" : "New shop"}
          </Typography>

          <Typography
            sx={{
              mt: 0.35,
              color: "#7A8696",
              fontSize: "0.64rem",
            }}
          >
            Configure identity, brand, location and network access.
          </Typography>
        </Paper>

        <Paper
          variant="outlined"
          sx={{
            p: 2,
            borderRadius: 0,
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "1fr 1fr",
              },
              gap: 1.2,
            }}
          >
            <Controller
              name="shop_code"
              control={control}
              rules={{
                required: "Shop code is required.",
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Shop code"
                  fullWidth
                  error={Boolean(errors.shop_code)}
                  helperText={errors.shop_code?.message}
                  sx={fieldSx}
                />
              )}
            />

            <Controller
              name="shop_name"
              control={control}
              rules={{
                required: "Shop name is required.",
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Shop name"
                  fullWidth
                  error={Boolean(errors.shop_name)}
                  helperText={errors.shop_name?.message}
                  sx={fieldSx}
                />
              )}
            />

            <Controller
              name="brand_id"
              control={control}
              rules={{
                required: "Brand is required.",
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  label="Brand"
                  fullWidth
                  disabled={brandsLoading || brandsError}
                  error={Boolean(errors.brand_id || brandsError)}
                  helperText={
                    errors.brand_id?.message ||
                    (brandsLoading
                      ? "Loading brands..."
                      : brandsError
                        ? "Unable to load brands."
                        : !brands.length
                          ? "No active brands are available."
                          : "")
                  }
                  sx={fieldSx}
                >
                  <MenuItem value="">
                    <em>Select brand</em>
                  </MenuItem>

                  {brands.map((brand) => (
                    <MenuItem key={brand.id} value={brand.id}>
                      {brand.name}
                      {brand.code ? ` (${brand.code})` : ""}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />

            <Controller
              name="public_ip_address"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Public IP address"
                  placeholder="102.89.12.10"
                  fullWidth
                  sx={fieldSx}
                />
              )}
            />

            <Controller
              name="location"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Location"
                  multiline
                  minRows={3}
                  fullWidth
                  sx={{
                    ...fieldSx,
                    gridColumn: {
                      md: "1 / -1",
                    },
                  }}
                />
              )}
            />
          </Box>

          <Controller
            name="is_active"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                sx={{ mt: 1 }}
                control={
                  <Switch
                    size="small"
                    checked={Boolean(field.value)}
                    onChange={(_, checked) => field.onChange(checked)}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      fontWeight: 700,
                    }}
                  >
                    Active shop
                  </Typography>
                }
              />
            )}
          />
        </Paper>
      </Box>

      <Stack
        direction="row"
        justifyContent="flex-end"
        spacing={1}
        sx={{ mt: 2 }}
      >
        <Button
          onClick={onCancel}
          color="inherit"
          variant="outlined"
          disabled={busy}
          sx={{ borderRadius: 0 }}
        >
          Cancel
        </Button>

        <Button
          type="submit"
          variant="contained"
          disabled={
            busy ||
            brandsLoading ||
            brandsError ||
            !brands.length
          }
          sx={{ borderRadius: 0 }}
        >
          {busy
            ? "Saving..."
            : shop
              ? "Save changes"
              : "Create shop"}
        </Button>
      </Stack>
    </Box>
  );
}

function RowMenu({ row, onView, onEdit, onWifiRules, onKey, onDelete }) {
  const [anchor, setAnchor] = useState(null);

  const choose = (handler) => {
    setAnchor(null);
    handler(row);
  };

  return (
    <>
      <IconButton
        size="small"
        onClick={(event) => setAnchor(event.currentTarget)}
      >
        <MoreVertRoundedIcon sx={{ fontSize: 17 }} />
      </IconButton>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        PaperProps={{
          sx: {
            borderRadius: 0,
            minWidth: 190,
          },
        }}
      >
        <MenuItem
          onClick={() => choose(onView)}
          sx={{ gap: 1, fontSize: "0.7rem" }}
        >
          <VisibilityOutlinedIcon sx={{ fontSize: 16 }} />
          View details
        </MenuItem>

        <MenuItem
          onClick={() => choose(onEdit)}
          sx={{ gap: 1, fontSize: "0.7rem" }}
        >
          <EditOutlinedIcon sx={{ fontSize: 16 }} />
          Edit shop
        </MenuItem>

        <MenuItem
          onClick={() => choose(onWifiRules)}
          sx={{ gap: 1, fontSize: "0.7rem" }}
        >
          <WifiRoundedIcon sx={{ fontSize: 16 }} />
          Wi-Fi rule management
        </MenuItem>

        <MenuItem
          onClick={() => choose(onKey)}
          sx={{ gap: 1, fontSize: "0.7rem" }}
        >
          <KeyRoundedIcon sx={{ fontSize: 16 }} />
          Generate API key
        </MenuItem>

        <MenuItem
          onClick={() => choose(onDelete)}
          sx={{
            gap: 1,
            color: "error.main",
            fontSize: "0.7rem",
          }}
        >
          <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />
          Delete shop
        </MenuItem>
      </Menu>
    </>
  );
}

export function ShopsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });

  const [dialog, setDialog] = useState({
    type: null,
    shop: null,
  });

  const [credentials, setCredentials] = useState(null);
  const [message, setMessage] = useState(null);

  const [keyForm, setKeyForm] = useState({
    name: "",
    expires_at: "",
  });

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setPaginationModel((current) => ({
        ...current,
        page: 0,
      }));
    }, 450);

    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const brandsQuery = useQuery({
    queryKey: ["shop-brand-options"],
    queryFn: () =>
      getBrands({
        page: 1,
        per_page: 100,
        is_active: 1,
      }),
    staleTime: 5 * 60 * 1000,
  });

  const brands = useMemo(
    () => brandsFrom(brandsQuery.data),
    [brandsQuery.data],
  );

  const shopsQuery = useQuery({
    queryKey: [
      "shops",
      search,
      paginationModel.page,
      paginationModel.pageSize,
    ],
    queryFn: () =>
      getShops({
        search: search || undefined,
        page: paginationModel.page + 1,
        per_page: paginationModel.pageSize,
      }),
    placeholderData: (previous) => previous,
  });

  const rows = rowsFrom(shopsQuery.data);
  const total = totalFrom(shopsQuery.data);

  const detailsQuery = useQuery({
    queryKey: ["shop", dialog.shop?.id],
    queryFn: () => getShop(dialog.shop.id),
    enabled:
      dialog.type === "view" && Boolean(dialog.shop?.id),
  });

  const detailShop =
    detailsQuery.data?.shop ??
    detailsQuery.data?.data?.shop ??
    dialog.shop;

  const saveMutation = useMutation({
    mutationFn: ({ shop, values }) =>
      shop
        ? updateShop(shop.id, values)
        : createShop(values),

    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: ["shops"] });

      setDialog({
        type: null,
        shop: null,
      });

      if (!variables.shop && response?.api?.api_key) {
        const bundle = {
          api: response.api,
          shop: response.shop,
        };

        setCredentials(bundle);

        window.setTimeout(() => {
          downloadCredentials(bundle.api, bundle.shop);
        }, 200);

        return;
      }

      setMessage({
        title: "Shop saved",
        text:
          response?.message ||
          "The shop was saved successfully.",
      });
    },

    onError: (error) =>
      setMessage({
        title: "Unable to save shop",
        text: getApiErrorMessage(error),
      }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => deleteShop(id),

    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["shops"] });

      setDialog({
        type: null,
        shop: null,
      });

      setMessage({
        title: "Shop deleted",
        text:
          response?.message ||
          "Shop deleted successfully.",
      });
    },

    onError: (error) =>
      setMessage({
        title: "Unable to delete shop",
        text: getApiErrorMessage(error),
      }),
  });

  const keyMutation = useMutation({
    mutationFn: () =>
      generateShopApiKey(dialog.shop.id, {
        name: keyForm.name.trim() || undefined,
        expires_at: keyForm.expires_at || undefined,
      }),

    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["shops"] });

      const bundle = {
        api: response.api,
        shop: dialog.shop,
      };

      setDialog({
        type: null,
        shop: null,
      });

      setCredentials(bundle);

      setKeyForm({
        name: "",
        expires_at: "",
      });

      window.setTimeout(() => {
        downloadCredentials(bundle.api, bundle.shop);
      }, 200);
    },

    onError: (error) =>
      setMessage({
        title: "Unable to generate API key",
        text: getApiErrorMessage(error),
      }),
  });

  const columns = useMemo(
    () => [
      {
        field: "avatar",
        headerName: "",
        width: 54,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Avatar
            sx={{
              width: 28,
              height: 28,
              bgcolor: "#EEF3FF",
              color: "#2F5BEA",
              fontSize: "0.6rem",
              fontWeight: 850,
            }}
          >
            {initials(params.row.shop_name)}
          </Avatar>
        ),
      },
      {
        field: "shop_code",
        headerName: "Shop code",
        minWidth: 130,
        flex: 0.6,
      },
      {
        field: "shop_name",
        headerName: "Shop name",
        minWidth: 210,
        flex: 1,
        renderCell: (params) => (
          <Typography
            sx={{
              fontSize: "0.72rem",
              fontWeight: 800,
            }}
          >
            {params.row.shop_name}
          </Typography>
        ),
      },
      {
        field: "brand_name",
        headerName: "Brand",
        minWidth: 150,
        flex: 0.7,
        valueGetter: (_, row) => brandName(row),
      },
      {
        field: "location",
        headerName: "Location",
        minWidth: 220,
        flex: 1.1,
        valueGetter: (_, row) => row.location || "—",
      },
      {
        field: "public_ip_address",
        headerName: "Public IP",
        minWidth: 140,
        valueGetter: (_, row) =>
          row.public_ip_address || "—",
      },
      {
        field: "api_keys",
        headerName: "API keys",
        width: 90,
        sortable: false,
        renderCell: (params) =>
          params.row.api_keys?.length || 0,
      },
      {
        field: "status",
        headerName: "Status",
        width: 90,
        renderCell: (params) => {
          const active = params.row.is_active ?? true;

          return (
            <Chip
              label={active ? "Active" : "Inactive"}
              size="small"
              sx={{
                height: 22,
                borderRadius: 0,
                bgcolor: active ? "#E7F6EE" : "#F1F3F5",
                color: active ? "#18794E" : "#667085",
                fontSize: "0.65rem",
                fontWeight: 750,
              }}
            />
          );
        },
      },
      {
        field: "created_at",
        headerName: "Created",
        minWidth: 120,
        valueFormatter: (value) =>
          value
            ? new Date(value).toLocaleDateString("en-ZA")
            : "—",
      },
      {
        field: "actions",
        headerName: "",
        width: 54,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <RowMenu
            row={params.row}
            onView={(shop) =>
              setDialog({
                type: "view",
                shop,
              })
            }
            onEdit={(shop) =>
              setDialog({
                type: "edit",
                shop,
              })
            }
            onWifiRules={(shop) =>
              navigate(`/admin/shops/${shop.id}/wifi-rules`, {
                state: { shop },
              })
            }
            onKey={(shop) => {
              setKeyForm({
                name: "",
                expires_at: "",
              });

              setDialog({
                type: "key",
                shop,
              });
            }}
            onDelete={(shop) =>
              setDialog({
                type: "delete",
                shop,
              })
            }
          />
        ),
      },
    ],
    [],
  );

  const doShare = async () => {
    try {
      const shared = await shareCredentials(
        credentials.api,
        credentials.shop,
      );

      if (!shared) {
        downloadCredentials(credentials.api, credentials.shop);

        setMessage({
          title: "Attachment downloaded",
          text:
            "File sharing is unavailable in this browser, so the credential attachment was downloaded.",
        });
      }
    } catch (error) {
      if (error?.name !== "AbortError") {
        setMessage({
          title: "Unable to share",
          text:
            "Download the credential file and attach it manually.",
        });
      }
    }
  };

  const copyCredentials = async () => {
    await navigator.clipboard.writeText(
      credentialText(credentials.api, credentials.shop),
    );

    setMessage({
      title: "Credentials copied",
      text:
        "The API credentials were copied to the clipboard.",
    });
  };

  return (
    <Box
      sx={{
        p: {
          xs: 1.2,
          md: 2,
        },
      }}
    >
      <Paper variant="outlined" sx={{ borderRadius: 0 }}>
        <Stack
          direction={{
            xs: "column",
            md: "row",
          }}
          spacing={1}
          sx={{
            p: 1.2,
            borderBottom: "1px solid #E3E7EC",
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                fontSize: "0.82rem",
                fontWeight: 850,
              }}
            >
              Shops
            </Typography>

            <Typography
              sx={{
                mt: 0.25,
                color: "#667085",
                fontSize: "0.66rem",
              }}
            >
              Manage shops, network details and API credentials.
            </Typography>
          </Box>

          <TextField
            size="small"
            value={searchInput}
            onChange={(event) =>
              setSearchInput(event.target.value)
            }
            placeholder="Search code, name, brand or location"
            sx={{
              width: {
                xs: "100%",
                md: 330,
              },
              "& .MuiInputBase-root": {
                height: 36,
                borderRadius: 0,
                fontSize: "0.7rem",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon sx={{ fontSize: 16 }} />
                </InputAdornment>
              ),
            }}
          />

          <Button
            variant="contained"
            startIcon={<AddRoundedIcon />}
            onClick={() =>
              setDialog({
                type: "create",
                shop: null,
              })
            }
            sx={{ borderRadius: 0 }}
          >
            New shop
          </Button>
        </Stack>

        <SoftDataGrid
          rows={rows}
          columns={columns}
          loading={shopsQuery.isLoading}
          rowCount={total}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20, 50, 100]}
          getRowId={(row) => row.id}
        />
      </Paper>

      <SquareDialog
        open={dialog.type === "create" || dialog.type === "edit"}
        onClose={() =>
          setDialog({
            type: null,
            shop: null,
          })
        }
        title={dialog.type === "edit" ? "Edit shop" : "Create shop"}
        subtitle={
          dialog.type === "create"
            ? "The API key will download automatically and will only be shown once."
            : "Update shop details."
        }
      >
        <ShopForm
          key={`${dialog.type}-${dialog.shop?.id || "new"}`}
          shop={dialog.type === "edit" ? dialog.shop : null}
          brands={brands}
          brandsLoading={brandsQuery.isLoading}
          brandsError={brandsQuery.isError}
          busy={saveMutation.isPending}
          onCancel={() =>
            setDialog({
              type: null,
              shop: null,
            })
          }
          onSubmit={(values) =>
            saveMutation.mutate({
              shop:
                dialog.type === "edit"
                  ? dialog.shop
                  : null,
              values,
            })
          }
        />
      </SquareDialog>

      <SquareDialog
        open={dialog.type === "view"}
        onClose={() =>
          setDialog({
            type: null,
            shop: null,
          })
        }
        title="Shop details"
        subtitle="Shop and API-key records."
        actions={
          <Button
            onClick={() =>
              setDialog({
                type: null,
                shop: null,
              })
            }
            sx={{ borderRadius: 0 }}
          >
            Close
          </Button>
        }
      >
        {detailShop ? (
          <Stack spacing={1}>
            <Typography
              sx={{
                fontSize: "0.9rem",
                fontWeight: 850,
              }}
            >
              {detailShop.shop_name}
            </Typography>

            <Typography sx={{ fontSize: "0.7rem" }}>
              Code: {detailShop.shop_code}
            </Typography>

            <Typography sx={{ fontSize: "0.7rem" }}>
              Brand: {brandName(detailShop)}
            </Typography>

            <Typography sx={{ fontSize: "0.7rem" }}>
              Location: {detailShop.location || "—"}
            </Typography>

            <Typography sx={{ fontSize: "0.7rem" }}>
              Public IP: {detailShop.public_ip_address || "—"}
            </Typography>

            <Typography
              sx={{
                mt: 1,
                fontSize: "0.7rem",
                fontWeight: 850,
              }}
            >
              API keys
            </Typography>

            {(detailShop.api_keys || []).length ? (
              (detailShop.api_keys || []).map((key) => (
                <Paper
                  key={key.id}
                  variant="outlined"
                  sx={{
                    p: 1,
                    borderRadius: 0,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      fontWeight: 800,
                    }}
                  >
                    {key.name}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: "0.62rem",
                      color: "#667085",
                    }}
                  >
                    {key.is_active ? "Active" : "Inactive"} · Expires{" "}
                    {key.expires_at || "never"}
                  </Typography>
                </Paper>
              ))
            ) : (
              <Typography
                sx={{
                  color: "#667085",
                  fontSize: "0.68rem",
                }}
              >
                No API keys found.
              </Typography>
            )}
          </Stack>
        ) : null}
      </SquareDialog>

      <SquareDialog
        open={dialog.type === "key"}
        onClose={() =>
          setDialog({
            type: null,
            shop: null,
          })
        }
        title="Generate API key"
        subtitle="The plaintext key will only be shown once."
        maxWidth="sm"
        actions={
          <>
            <Button
              onClick={() =>
                setDialog({
                  type: null,
                  shop: null,
                })
              }
              color="inherit"
              sx={{ borderRadius: 0 }}
            >
              Cancel
            </Button>

            <Button
              onClick={() => keyMutation.mutate()}
              variant="contained"
              disabled={keyMutation.isPending}
              sx={{ borderRadius: 0 }}
            >
              {keyMutation.isPending
                ? "Generating..."
                : "Generate key"}
            </Button>
          </>
        }
      >
        <Stack spacing={1.2}>
          <TextField
            label="Credential name"
            value={keyForm.name}
            onChange={(event) =>
              setKeyForm((current) => ({
                ...current,
                name: event.target.value,
              }))
            }
            fullWidth
            sx={fieldSx}
          />

          <TextField
            label="Expiry date"
            type="datetime-local"
            value={keyForm.expires_at}
            onChange={(event) =>
              setKeyForm((current) => ({
                ...current,
                expires_at: event.target.value,
              }))
            }
            InputLabelProps={{ shrink: true }}
            fullWidth
            sx={fieldSx}
          />
        </Stack>
      </SquareDialog>

      <SquareDialog
        open={dialog.type === "delete"}
        onClose={() =>
          setDialog({
            type: null,
            shop: null,
          })
        }
        title="Delete shop"
        subtitle="This action cannot be undone."
        maxWidth="sm"
        actions={
          <>
            <Button
              onClick={() =>
                setDialog({
                  type: null,
                  shop: null,
                })
              }
              color="inherit"
              sx={{ borderRadius: 0 }}
            >
              Cancel
            </Button>

            <Button
              onClick={() =>
                deleteMutation.mutate(dialog.shop.id)
              }
              color="error"
              variant="contained"
              disabled={deleteMutation.isPending}
              sx={{ borderRadius: 0 }}
            >
              {deleteMutation.isPending
                ? "Deleting..."
                : "Delete shop"}
            </Button>
          </>
        }
      >
        <Typography sx={{ fontSize: "0.72rem" }}>
          Delete <strong>{dialog.shop?.shop_name}</strong> and its
          API-key records?
        </Typography>
      </SquareDialog>

      <SquareDialog
        open={Boolean(credentials)}
        onClose={() => {}}
        title="Save API credentials now"
        subtitle="This key will not be available again."
        maxWidth="sm"
        actions={
          <>
            <Button
              onClick={copyCredentials}
              startIcon={<ContentCopyRoundedIcon />}
              color="inherit"
              sx={{ borderRadius: 0 }}
            >
              Copy
            </Button>

            <Button
              onClick={doShare}
              startIcon={<ShareRoundedIcon />}
              variant="outlined"
              sx={{ borderRadius: 0 }}
            >
              Share attachment
            </Button>

            <Button
              onClick={() =>
                downloadCredentials(
                  credentials.api,
                  credentials.shop,
                )
              }
              startIcon={<DownloadRoundedIcon />}
              variant="contained"
              sx={{ borderRadius: 0 }}
            >
              Download again
            </Button>

            <Button
              onClick={() => setCredentials(null)}
              color="inherit"
              sx={{ borderRadius: 0 }}
            >
              I have saved it
            </Button>
          </>
        }
      >
        {credentials ? (
          <Stack spacing={1.2}>
            <Paper
              variant="outlined"
              sx={{
                p: 1.2,
                borderRadius: 0,
                bgcolor: "#FFF8F7",
                borderColor: "#F2B8B5",
              }}
            >
              <Typography
                sx={{
                  color: "#B42318",
                  fontSize: "0.7rem",
                  fontWeight: 850,
                }}
              >
                {credentials.api.warning}
              </Typography>
            </Paper>

            <TextField
              label="API endpoint"
              value={credentials.api.url}
              fullWidth
              InputProps={{ readOnly: true }}
              sx={fieldSx}
            />

            <TextField
              label="Header"
              value={credentials.api.header}
              fullWidth
              InputProps={{ readOnly: true }}
              sx={fieldSx}
            />

            <TextField
              label="API key"
              value={credentials.api.api_key}
              multiline
              minRows={3}
              fullWidth
              InputProps={{ readOnly: true }}
              sx={fieldSx}
            />
          </Stack>
        ) : null}
      </SquareDialog>

      <Dialog
        open={Boolean(message)}
        onClose={() => setMessage(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 0,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: "0.82rem",
            fontWeight: 850,
          }}
        >
          {message?.title}
        </DialogTitle>

        <DialogContent dividers>
          <Typography sx={{ fontSize: "0.7rem" }}>
            {message?.text}
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setMessage(null)}
            variant="contained"
            sx={{ borderRadius: 0 }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
